/*
 * Alerts & Notifications tab — governs how loud the agency gets about
 * tardies and other operational alerts. Currently exposes:
 *   • Tardy re-notification interval (minutes). 0 = single alert only.
 *   • Open-shift tardy toggle — surface unassigned shifts as tardies.
 * Backed by `PATCH /api/agencies/alert-settings`.
 */
import { useEffect, useState } from "react";
import api, { errorMessage } from "../../lib/api";
import { ActionButton, FormField, inputClass } from "../Primitives";
import { toast } from "sonner";
import { Bell, Loader2 } from "lucide-react";

export function AlertsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [interval, setIntervalMin] = useState(15);
  const [alertOpen, setAlertOpen] = useState(true);
  const [shortRestHours, setShortRestHours] = useState(6);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/agencies/me");
        const as = r.data?.alert_settings || {};
        setIntervalMin(as.tardy_notify_interval_minutes ?? 15);
        setAlertOpen(as.tardy_alert_open_shifts !== false);
        setShortRestHours(typeof as.short_rest_hours === "number" ? as.short_rest_hours : 6);
      } catch (e) {
        toast.error(errorMessage(e, "Failed to load alert settings"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/agencies/alert-settings", {
        tardy_notify_interval_minutes: Number(interval) || 0,
        tardy_alert_open_shifts: !!alertOpen,
        short_rest_hours: Math.max(0, Math.min(12, Number(shortRestHours) || 0)),
      });
      toast.success("Alert settings saved");
    } catch (e) {
      toast.error(errorMessage(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-zinc-500 flex items-center justify-center gap-2">
        <Loader2 className="size-4 animate-spin" /> Loading alert settings…
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="alerts-tab">
      <div className="border border-[#27272A] bg-[#0A0A0A] p-5">
        <div className="label-mute mb-3 flex items-center gap-2"><Bell className="size-3" /> Tardy Alerts</div>
        <FormField label="Re-notification interval (minutes)">
          <input
            data-testid="alert-tardy-interval"
            type="number"
            min="0"
            max="240"
            step="5"
            className={inputClass}
            value={interval}
            onChange={(e) => setIntervalMin(e.target.value)}
          />
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
            Alerts re-fire every N minutes while a shift stays un-clocked-in — until the guard clocks in or the shift ends.
            {" "}Set to <span className="text-[#FF3B30]">0</span> to only send the initial 15-min-late alert.
            {" "}Values under 5 are clamped to 5.
          </div>
        </FormField>
        <label className="flex items-start gap-2 cursor-pointer select-none mt-4">
          <input
            data-testid="alert-open-shifts"
            type="checkbox"
            className="mt-0.5 accent-[#FF3B30]"
            checked={alertOpen}
            onChange={(e) => setAlertOpen(e.target.checked)}
          />
          <span className="text-sm text-zinc-300">
            Alert on open (unassigned) shifts too
            <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
              When a shift starts without any assigned , treat it as a tardy and page the recipients on this cadence.
            </span>
          </span>
        </label>
      </div>
      <div className="border border-[#27272A] bg-[#0A0A0A] p-5" data-testid="short-rest-panel">
        <div className="label-mute mb-3 flex items-center gap-2"><Bell className="size-3" /> Short-rest warning</div>
        <div className="text-xs text-zinc-400 mb-3">
          Flag a shift with a red <span className="text-[#FF3B30] font-bold">SHORT REST</span> pill when the assigned officer&rsquo;s previous shift ended less than this many hours before it starts. Zero disables the warning entirely.
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="short-rest-chips">
          {[0, 6, 8, 10, 12].map((h) => {
            const on = Number(shortRestHours) === h;
            const label = h === 0 ? "Off" : `${h}h`;
            return (
              <button
                type="button"
                key={h}
                data-testid={`short-rest-chip-${h}`}
                onClick={() => setShortRestHours(h)}
                className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors ${
                  on
                    ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                    : "border-[#27272A] text-zinc-400 hover:border-[#FF3B30]"
                }`}
              >
                {label}
              </button>
            );
          })}
          <div className="ml-2 flex items-center gap-1">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Custom</span>
            <input
              type="number"
              min="0"
              max="12"
              step="1"
              data-testid="short-rest-custom"
              className={`${inputClass} w-16 text-center`}
              value={shortRestHours}
              onChange={(e) => setShortRestHours(Math.max(0, Math.min(12, Number(e.target.value) || 0)))}
            />
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">h</span>
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <ActionButton data-testid="save-alert-settings" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save Alert Settings"}
        </ActionButton>
      </div>
    </div>
  );
}
